import {
  type WebhookEndpoint,
  type WebhookPreset,
  type WebhookSamplePayload,
} from '@littlebird/sdk';
import { useState } from 'react';

import { type ResolvableModalFC } from '@/components/modals/modalsManager';
import { Input } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import TextArea from '@/components/ui/TextArea';

import { WebhookPresetMenu } from './components/webhook-preset-menu';
import { WebhookSamplePayloadField } from './components/webhook-sample-payload-field';
import { parseSamplePayload } from './webhook-sample-payload';

export type WebhookEndpointFormResult = {
  name: string;
  description: string;
  /** The team the endpoint is shared with, or null for the user's own; absent when the user has no team to choose. */
  teamId?: string | null;
  /** A new sample, or null for none; absent when an edit keeps the endpoint's suggested fields. */
  samplePayload?: WebhookSamplePayload | null;
};

type WebhookEndpointTeam = { id: string; name: string };

export type WebhookEndpointFormModalProps = {
  team: WebhookEndpointTeam | null;
  /** The endpoint to edit; absent when creating one. */
  endpoint?: WebhookEndpoint;
  /** Services a new endpoint can start from; empty when editing, or when they didn't load. */
  presets?: WebhookPreset[];
};

const fieldCount = (count: number) =>
  `${count} ${count === 1 ? 'field' : 'fields'}`;

/** Resolves to the form's values, or null when the user cancels. */
export const WebhookEndpointFormModal: ResolvableModalFC<
  WebhookEndpointFormModalProps,
  WebhookEndpointFormResult | null
> = ({ open, onOpenChange, resolve, team, endpoint, presets = [] }) => {
  const [preset, setPreset] = useState<WebhookPreset | null>(null);
  const [name, setName] = useState(endpoint?.name ?? '');
  const [description, setDescription] = useState(endpoint?.description ?? '');
  const [sampleText, setSampleText] = useState('');
  const [removesFields, setRemovesFields] = useState(false);
  const [sharedWithTeam, setSharedWithTeam] = useState(
    Boolean(endpoint?.teamId)
  );
  const trimmedName = name.trim();
  const parsedSample = parseSamplePayload(sampleText);
  const existingFields = endpoint?.payloadFields ?? null;

  const close = (result: WebhookEndpointFormResult | null) => {
    resolve(result);
    onOpenChange(false);
  };

  const startFrom = (chosen: WebhookPreset) => {
    setPreset(chosen);
    setName(chosen.label);
    setDescription(chosen.description);
    setSampleText(JSON.stringify(chosen.samplePayload, null, 2));
  };

  const samplePayloadResult = (sample: WebhookSamplePayload | null) => {
    if (!endpoint) return sample;
    if (removesFields) return null;
    return sample ?? undefined;
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) resolve(null);
        onOpenChange(next);
      }}
    >
      <Dialog.Content className="max-w-md">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (trimmedName && parsedSample.ok) {
              close({
                name: trimmedName,
                description: description.trim(),
                teamId: team ? (sharedWithTeam ? team.id : null) : undefined,
                samplePayload: samplePayloadResult(parsedSample.sample),
              });
            }
          }}
        >
          <Dialog.Header>
            <Dialog.Title>
              {endpoint ? `Edit ${endpoint.name}` : 'New webhook endpoint'}
            </Dialog.Title>
            <Dialog.Description>
              {endpoint
                ? 'Its URL and secret stay the same.'
                : 'Name it after the service that will post to it. Any of your routines can then use the same endpoint.'}
            </Dialog.Description>
          </Dialog.Header>
          <YStack className="my-3 gap-3">
            {presets.length > 0 ? (
              <YStack className="gap-1">
                <WebhookPresetMenu
                  presets={presets}
                  selected={preset}
                  onSelect={startFrom}
                />
                <span className="text-xs text-foreground-muted">
                  {preset
                    ? preset.setup
                    : 'Optional. Fills in the name, description and a sample payload for a service such as GitHub or Stripe.'}
                </span>
              </YStack>
            ) : null}
            <Input
              aria-label="Endpoint name"
              placeholder="e.g. Stripe"
              autoFocus
              maxLength={100}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <YStack className="gap-1">
              <TextArea
                aria-label="Description"
                placeholder="e.g. Invoice events from our Stripe account"
                rows={2}
                maxLength={250}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
              <span className="text-xs text-foreground-muted">
                Optional. Littlebird reads this to understand what the service
                posts here, and to choose this endpoint for a routine.
              </span>
            </YStack>
            {existingFields !== null ? (
              <XStack className="items-center justify-between gap-2 text-sm">
                <span className="text-foreground-muted">
                  {removesFields
                    ? 'The suggested fields are removed when you save.'
                    : `${fieldCount(existingFields.length)} from an earlier sample. Paste a new one to replace them.`}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setRemovesFields(!removesFields);
                    setSampleText('');
                  }}
                >
                  {removesFields ? 'Keep fields' : 'Remove fields'}
                </Button>
              </XStack>
            ) : null}
            {removesFields ? null : (
              <WebhookSamplePayloadField
                value={sampleText}
                error={parsedSample.ok ? null : parsedSample.error}
                onChange={setSampleText}
              />
            )}
            {team ? (
              <XStack className="items-start gap-2">
                <Checkbox
                  id="webhook-endpoint-share-with-team"
                  checked={sharedWithTeam}
                  onCheckedChange={(checked) =>
                    setSharedWithTeam(checked === true)
                  }
                  className="mt-0.5"
                />
                <label
                  htmlFor="webhook-endpoint-share-with-team"
                  className="text-sm"
                >
                  Share with {team.name}
                  <span className="block text-foreground-muted">
                    Everyone on your team can start their routines from it. The
                    teammate who shares it and your team admins can change or
                    delete it. Only the teammate who shares it can stop sharing
                    it, once no teammate&apos;s routine uses it.
                  </span>
                </label>
              </XStack>
            ) : null}
          </YStack>
          <Dialog.Footer>
            <Button type="button" variant="ghost" onClick={() => close(null)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!trimmedName || !parsedSample.ok}>
              {endpoint ? 'Save' : 'Create'}
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};
